import React, { FormEvent, useEffect, useState } from "react";
import Modal from "../../../components/ui/modal";
import PhoneInput, {
  getCountries,
  type Country,
} from "react-phone-number-input";
import phoneCountryLabels from "react-phone-number-input/locale/en.json";
import "react-phone-number-input/style.css";
import { useAppDispatch, useAppSelector } from "../../../store";
import {
  create_compliance_request,
  get_compliances,
} from "../../../service/api/compliance";
import { get_countries } from "../../../service/api/locations";
import { get_services } from "../../../service/api/service";
import { ICreateComplianceRequest } from "../../../store/@types/compliance";
import ServiceMultiSelect from "./ServiceMultiSelect";

const normalizeCountryName = (name: string) =>
  name
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const phoneCountryByName = new Map<string, Country>(
  getCountries().map((country) => [
    normalizeCountryName(phoneCountryLabels[country] || ""),
    country,
  ]),
);

type CreateComplianceModalProps = {
  open: boolean;
  onClose: () => void;
};

type ComplianceFormData = Omit<
  ICreateComplianceRequest,
  "selected_country" | "selected_services"
> & {
  selected_country: string;
  selected_services: string[];
};

const emptyForm: ComplianceFormData = {
  selected_country: "",
  selected_services: [],
  company_name: "",
  contact_email: "",
  business_type: "",
  company_address: "",
  contact_phone: "",
};

const CreateComplianceModal: React.FC<CreateComplianceModalProps> = ({
  open,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const loading = useAppSelector(({ compliance }) => compliance.loading);
  const countries = useAppSelector(({ locations }) => locations.countries);
  const countriesLoading = useAppSelector(({ locations }) => locations.loading);
  const services = useAppSelector(({ service }) => service.services);
  const servicesLoading = useAppSelector(({ service }) => service.loading);
  const [formData, setFormData] = useState<ComplianceFormData>(emptyForm);
  const [serviceError, setServiceError] = useState(false);
  const selectedCountry = countries.find(
    (country) => String(country.id) === formData.selected_country,
  );
  const phoneDefaultCountry = selectedCountry
    ? phoneCountryByName.get(normalizeCountryName(selectedCountry.name))
    : undefined;

  useEffect(() => {
    if (countries.length === 0) {
      void get_countries(dispatch);
    }
  }, [countries.length, dispatch]);

  useEffect(() => {
    if (services.length === 0) {
      void get_services(dispatch, 100, 0);
    }
  }, [dispatch, services.length]);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (formData.selected_services.length === 0) {
      setServiceError(true);
      return;
    }

    const created = await create_compliance_request(dispatch, {
      ...formData,
      selected_country: Number(formData.selected_country),
      selected_services: formData.selected_services.map(Number),
    });

    if (created) {
      onClose();
      setFormData(emptyForm);
      setServiceError(false);
      await get_compliances(dispatch);
    }
  };
  return (
    <Modal open={open} onClose={onClose}>
      <form className="space-y-4" onSubmit={handleSubmit}>
        <div>
          <h2 className="text-xl font-semibold text-gray-900">
            Create compliance request
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Provide your company details and choose the services you need.
          </p>
        </div>

        <label className="block text-sm font-medium text-gray-700">
          Country
          <select
            required
            value={formData.selected_country}
            onChange={(event) =>
              setFormData((previous) => ({
                ...previous,
                selected_country: event.target.value,
                contact_phone: "",
              }))
            }
            className="mt-1 w-full rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
          >
            <option value="">Select a country</option>
            {countries.map((country) => (
              <option key={country.id} value={country.id}>
                {country.name}
              </option>
            ))}
          </select>
          {countriesLoading && (
            <span className="mt-1 block text-xs text-gray-500">
              Loading countries…
            </span>
          )}
        </label>

        <label className="block text-sm font-medium text-gray-700">
          Services
          <ServiceMultiSelect
            services={services}
            value={formData.selected_services}
            error={serviceError}
            onChange={(selected_services) => {
              setServiceError(false);
              setFormData((previous) => ({ ...previous, selected_services }));
            }}
          />
          {servicesLoading && (
            <span className="mt-1 block text-xs text-gray-500">
              Loading services…
            </span>
          )}
          <span className="mt-1 block text-xs text-gray-500">
            Select one or more services.
          </span>
        </label>

        <label className="block text-sm font-medium text-gray-700">
          Company name
          <input
            required
            value={formData.company_name}
            onChange={(event) =>
              setFormData((previous) => ({
                ...previous,
                company_name: event.target.value,
              }))
            }
            className="mt-1 h-11 w-full rounded-lg border border-gray-200 px-3 text-sm outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
          />
        </label>

        <label className="block text-sm font-medium text-gray-700">
          Business type
          <input
            required
            value={formData.business_type}
            onChange={(event) =>
              setFormData((previous) => ({
                ...previous,
                business_type: event.target.value,
              }))
            }
            className="mt-1 h-11 w-full rounded-lg border border-gray-200 px-3 text-sm outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
          />
        </label>

        <label className="block text-sm font-medium text-gray-700">
          Company address
          <textarea
            required
            value={formData.company_address}
            onChange={(event) =>
              setFormData((previous) => ({
                ...previous,
                company_address: event.target.value,
              }))
            }
            rows={3}
            className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
          />
        </label>

        <label className="block text-sm font-medium text-gray-700">
          Contact email
          <input
            required
            type="email"
            value={formData.contact_email}
            onChange={(event) =>
              setFormData((previous) => ({
                ...previous,
                contact_email: event.target.value,
              }))
            }
            className="mt-1 h-11 w-full rounded-lg border border-gray-200 px-3 text-sm outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-100"
          />
        </label>

        <label className="block text-sm font-medium text-gray-700">
          Contact phone
          <PhoneInput
            international
            defaultCountry={phoneDefaultCountry}
            value={formData.contact_phone || undefined}
            onChange={(value) =>
              setFormData((previous) => ({
                ...previous,
                contact_phone: value ?? "",
              }))
            }
            className="PhoneInput mt-1 flex h-11 w-full items-center gap-2 rounded-lg border border-gray-200 px-3 focus-within:border-primary-500 focus-within:ring-2 focus-within:ring-primary-100"
            numberInputProps={{
              required: true,
              className:
                "PhoneInputInput w-full !outline-none px-0 py-0 text-sm border-none focus:border-transparent focus:ring-0",
            }}
          />
        </label>

        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={loading}
            className="rounded-lg bg-primary-500 px-4 py-2.5 text-sm font-medium text-white hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? "Submitting…" : "Submit request"}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateComplianceModal;
